import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentIcon from '@mui/icons-material/Payment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Card, Stack, Container, Typography, TablePagination, IconButton, Chip,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  TextField, InputAdornment, List, ListItem, ListItemText, Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Page from '../../components/Page';
import { ListToolbar } from '../../components/tables';
import SortDropdown from '../../components/SortDropdown';
import tracker from '../../api/tracker';
import AgGridWrapper, { agDefaultColDef } from '../../components/tables/AgGridWrapper';
import useNotification from '../../hooks/useNotificaction';
import PaymentQuotePreview, { fetchSacramentPaymentQuote } from '../../sections/@dashboard/sacraments/PaymentQuotePreview';

const override = { margin: '0 auto' };

const currentProgramYearStart = (date = new Date()) => {
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return month >= 8 ? year : year - 1;
};

const buildProgramYearOptions = (selected, allLabel, date = new Date()) => {
  const start = currentProgramYearStart(date);
  const years = [];
  for (let year = start + 1; year >= start - 8; year -= 1) {
    years.push(`${year}-${year + 1}`);
  }
  if (selected && selected !== 'all' && !years.includes(selected)) {
    years.unshift(selected);
  }
  return [
    { value: 'all', label: allLabel },
    ...years.map((year) => ({ value: year, label: year })),
  ];
};

const listSearchFromState = (filter, sacramentFilter, statusFilter, religiousEducationFilter, yearFilter, page) => {
  const next = new URLSearchParams();
  if (filter) next.set('search', filter);
  if (sacramentFilter && sacramentFilter !== 'all') next.set('sacrament', sacramentFilter);
  if (statusFilter && statusFilter !== 'all') next.set('status', statusFilter);
  if (religiousEducationFilter && religiousEducationFilter !== 'all') {
    next.set('religious_education', religiousEducationFilter);
  }
  if (yearFilter && yearFilter !== 'all') next.set('year', yearFilter);
  if (page > 1) next.set('page', String(page));
  const qs = next.toString();
  return qs ? `?${qs}` : '';
};

const SACRAMENT_COLORS = {
  baptism:      '#dbeafe',
  communion:    '#dcfce7',
  confirmation: '#fef9c3',
  matrimony:    '#f3e8ff',
};

const STATUS_COLORS = {
  pending:   { background: '#fef9c3', color: '#854d0e' },
  active:    { background: '#dcfce7', color: '#166534' },
  completed: { background: '#dbeafe', color: '#1e40af' },
  rejected:  { background: '#fee2e2', color: '#991b1b' },
};

export default function SacramentRegIndex() {
  const [searchParams, setSearchParams]   = useSearchParams();
  const [records, setRecords]             = useState([]);
  const [pagination, setPagination]       = useState({ total: 0, per_page: 15, current_page: 1, last_page: 1 });
  const [page, setPage]                   = useState(() => {
    const parsed = Number(searchParams.get('page'));
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
  });
  const [filter, setFilter]               = useState(() => searchParams.get('search') || '');
  const [sacramentFilter, setSacramentFilter] = useState(() => searchParams.get('sacrament') || 'all');
  const [statusFilter, setStatusFilter]   = useState(() => searchParams.get('status') || 'all');
  const [religiousEducationFilter, setReligiousEducationFilter] = useState(
    () => searchParams.get('religious_education') || 'all'
  );
  const [yearFilter, setYearFilter] = useState(() => searchParams.get('year') || 'all');
  const [isLoading, setIsLoading]         = useState(true);
  const [selectedRows, setSelectedRows]   = useState([]);
  const [paymentRows, setPaymentRows]     = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [isGeneratingPayment, setIsGeneratingPayment] = useState(false);
  const [isExpandingSiblings, setIsExpandingSiblings] = useState(false);
  const [generatedPayment, setGeneratedPayment] = useState(null);
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  const { t }    = useTranslation();
  const navigate = useNavigate();
  const [sendNotification] = useNotification();

  const expandSiblingGroups = async (rows) => {
    const groupIds = [...new Set(rows.map((r) => r.sibling_group_id).filter(Boolean))];
    if (groupIds.length === 0) return rows;

    const byHash = new Map(rows.map((r) => [r.hashid, r]));
    await Promise.all(
      groupIds.map(async (groupId) => {
        const params = new URLSearchParams({
          sibling_group_id: groupId,
          page_size: 50,
          sort: 'created_at:desc',
        });
        const res = await tracker.get(`sacrament-registrations?${params.toString()}`);
        (res.data.data || []).forEach((r) => byHash.set(r.hashid, r));
      })
    );
    return [...byHash.values()];
  };

  const handleGeneratePaymentLink = async () => {
    if (paymentRows.length === 0) return;
    setIsGeneratingPayment(true);
    try {
      const res = await tracker.post('sacrament-payments', {
        registration_hashids: paymentRows.map((r) => r.hashid),
      });
      setGeneratedPayment(res.data.data);
      sendNotification({ msg: t('payment_link_generated') || 'Enlace de donación generado y enviado por correo', variant: 'success' });
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('error_generating_payment_link') || 'Error al generar el enlace de donación',
        variant: 'error',
      });
    } finally {
      setIsGeneratingPayment(false);
    }
  };

  const handleOpenPaymentDialog = async () => {
    setGeneratedPayment(null);
    setPaymentQuote(null);
    setQuoteError(null);
    setPaymentRows(selectedRows);
    setPaymentDialogOpen(true);
    setIsExpandingSiblings(true);
    try {
      const expanded = await expandSiblingGroups(selectedRows);
      setPaymentRows(expanded);
      setIsLoadingQuote(true);
      try {
        const quote = await fetchSacramentPaymentQuote(tracker, expanded.map((r) => r.hashid));
        setPaymentQuote(quote);
      } catch (error) {
        setQuoteError(
          error?.response?.data?.message || t('error_loading_payment_amount') || 'No se pudo cargar la cantidad a pagar'
        );
      } finally {
        setIsLoadingQuote(false);
      }
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('error_loading_siblings') || 'No se pudieron cargar los hermanos del grupo',
        variant: 'error',
      });
    } finally {
      setIsExpandingSiblings(false);
    }
  };

  const handleCopyLink = () => {
    if (generatedPayment?.payment_url) {
      navigator.clipboard.writeText(generatedPayment.payment_url);
      sendNotification({ msg: t('copied_to_clipboard') || 'Enlace copiado', variant: 'success' });
    }
  };

  const sacramentOptions = [
    { value: 'all',          label: t('all') },
    { value: 'baptism',      label: t('_tab_baptism') },
    { value: 'communion',    label: t('_tab_communion') },
    { value: 'confirmation', label: t('_tab_confirmation') },
    { value: 'matrimony',    label: t('_tab_matrimony') },
  ];

  const statusOptions = [
    { value: 'all',       label: t('all') },
    { value: 'pending',   label: t('pending') },
    { value: 'active',    label: t('active') },
    { value: 'completed', label: t('completed') },
    { value: 'rejected',  label: t('rejected') },
  ];

  const religiousEducationOptions = [
    { value: 'all', label: t('all') },
    { value: '1', label: t('with_religious_education') },
    { value: '0', label: t('without_religious_education') },
  ];

  const yearOptions = buildProgramYearOptions(yearFilter, t('all'));

  const applyYearFilter = (value) => {
    setYearFilter(value);
    setPage(1);
  };

  const refreshTable = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ page, sort: 'created_at:desc' });
      if (filter)                              params.append('search', filter);
      if (sacramentFilter && sacramentFilter !== 'all') params.append('sacrament_type', sacramentFilter);
      if (statusFilter    && statusFilter    !== 'all') params.append('status', statusFilter);
      if (religiousEducationFilter === '1' || religiousEducationFilter === '0') {
        params.append('requires_religious_education', religiousEducationFilter);
      }
      if (yearFilter && yearFilter !== 'all') params.append('program_year', yearFilter);
      const res = await tracker.get(`sacrament-registrations?${params.toString()}`);
      setRecords(res.data.data || []);
      if (res.data.meta) setPagination(res.data.meta);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(refreshTable, 400);
    return () => clearTimeout(delay);
  }, [filter, sacramentFilter, statusFilter, religiousEducationFilter, yearFilter, page]);

  useEffect(() => {
    const next = listSearchFromState(filter, sacramentFilter, statusFilter, religiousEducationFilter, yearFilter, page);
    const nextQs = next.startsWith('?') ? next.slice(1) : next;
    if (nextQs !== searchParams.toString()) {
      setSearchParams(new URLSearchParams(nextQs), { replace: true });
    }
  }, [filter, sacramentFilter, statusFilter, religiousEducationFilter, yearFilter, page]);

  const openView = (id) => {
    navigate(`/dashboard/sacrament-reg/view/${id}`, {
      state: {
        listSearch: listSearchFromState(filter, sacramentFilter, statusFilter, religiousEducationFilter, yearFilter, page),
      },
    });
  };

  function DonationCell({ value }) {
    return value
      ? <CheckIcon style={{ color: 'green', fontSize: 20 }} />
      : <CloseIcon style={{ color: 'red', fontSize: 20 }} />;
  }

  const columnDefs = [
    { field: 'full_name', headerName: t('name'), flex: 1, minWidth: 140 },
    {
      field: 'sacrament_type',
      headerName: t('sacrament'),
      width: 160,
      cellRenderer: (params) => {
        const label = t(`_tab_${params.value}`) || t(params.value) || params.value;
        return (
          <Chip
            label={label}
            size="small"
            style={{ background: SACRAMENT_COLORS[params.value] || '#f1f5f9', fontSize: '11px' }}
          />
        );
      },
    },
    {
      field: 'requires_religious_education',
      headerName: t('religious_education'),
      width: 170,
      sortable: false,
      filter: false,
      cellRenderer: (params) =>
        params.value ? (
          <Chip
            label={t('religious_education')}
            size="small"
            style={{ background: '#fce7f3', color: '#9d174d', fontSize: '11px', fontWeight: 600 }}
          />
        ) : (
          '—'
        ),
    },
    {
      field: 'form_type',
      headerName: t('type'),
      width: 110,
      cellRenderer: (params) => t(params.value) || params.value,
    },
    { field: 'program_year', headerName: t('year'), width: 100 },
    {
      field: 'sibling_group_id',
      headerName: t('siblings') || 'Hermanos',
      width: 140,
      sortable: false,
      filter: false,
      cellRenderer: (params) =>
        params.value ? (
          <Chip
            label={params.value}
            size="small"
            title={t('sibling_group') || 'Grupo de hermanos'}
            style={{ background: '#e0f2fe', color: '#075985', fontSize: '11px', fontWeight: 600 }}
          />
        ) : (
          '—'
        ),
    },
    { field: 'phone', headerName: t('phone'), width: 130 },
    {
      field: 'donation_made',
      headerName: t('donation_made'),
      width: 120,
      sortable: false,
      filter: false,
      cellRenderer: (params) => <DonationCell value={params.value} />,
    },
    {
      field: 'status',
      headerName: t('status'),
      width: 120,
      cellRenderer: (params) => {
        const label = t(params.value) || params.value;
        return (
          <Chip
            label={label}
            size="small"
            style={{ ...(STATUS_COLORS[params.value] || { background: '#f1f5f9' }), fontSize: '11px' }}
          />
        );
      },
    },
    {
      headerName: '',
      width: 60,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: (params) => (
        <IconButton size="small" color="primary" onClick={() => openView(params.data.hashid)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Page title={t('sacrament_regs')}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="flex-start" mb={5}>
          <Typography variant="h4" gutterBottom>{t('sacrament_regs')}</Typography>
        </Stack>
        <Card>
          <Stack direction="row" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
            <ListToolbar numSelected={0} filterName={filter} onFilterName={(e) => setFilter(e.target.value)} placeholder="Search name / phone..." />
            <SortDropdown sx={{ width: '180px' }} options={sacramentOptions} sort={sacramentFilter} setSort={setSacramentFilter} />
            <SortDropdown sx={{ width: '150px' }} options={statusOptions}    sort={statusFilter}    setSort={setStatusFilter}    />
            <SortDropdown
              sx={{ width: '220px' }}
              options={religiousEducationOptions}
              sort={religiousEducationFilter}
              setSort={setReligiousEducationFilter}
            />
            <SortDropdown
              sx={{ width: '160px' }}
              label={t('year')}
              options={yearOptions}
              sort={yearFilter}
              setSort={applyYearFilter}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<PaymentIcon />}
              disabled={selectedRows.length === 0}
              onClick={handleOpenPaymentDialog}
              sx={{ background: '#032C65', '&:hover': { background: '#021a40' }, ml: 'auto' }}
            >
              {selectedRows.length > 0
                ? `${t('generate_payment_link') || 'Generar enlace de donación'} (${selectedRows.length})`
                : (t('generate_payment_link') || 'Generar enlace de donación')}
            </Button>
          </Stack>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <FadeLoader color="blue" loading cssOverride={override} size={50} />
            </div>
          ) : (
            <AgGridWrapper
              rowData={records}
              columnDefs={columnDefs}
              defaultColDef={agDefaultColDef}
              rowSelection={{
                mode: 'multiRow',
                checkboxes: true,
                headerCheckbox: true,
                enableClickSelection: false,
              }}
              onSelectionChanged={(event) => setSelectedRows(event.api.getSelectedRows())}
            />
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page || 15}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
          />
        </Card>
      </Container>

      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('generate_payment_link') || 'Generar enlace de donación'}</DialogTitle>
        <DialogContent>
          {!generatedPayment ? (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {t('generate_payment_link_hint') ||
                  'Se creará un único enlace de donación que combina los conceptos (sacramento y/o educación religiosa) de los siguientes registros, aplicando descuentos por hermanos si corresponde. Se enviará por correo a cada registrante.'}
              </DialogContentText>
              {isExpandingSiblings && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('loading_siblings') || 'Incluyendo hermanos del mismo registro…'}
                </Typography>
              )}
              <List dense>
                {paymentRows.map((r) => (
                  <ListItem key={r.hashid} disableGutters>
                    <ListItemText
                      primary={r.full_name}
                      secondary={`${t(`_tab_${r.sacrament_type}`) || r.sacrament_type} — ${r.email || t('no_email') || 'sin correo'}${
                        r.sibling_group_id ? ` · ${t('sibling_group') || 'Grupo de hermanos'}` : ''
                      }`}
                    />
                  </ListItem>
                ))}
              </List>
              <PaymentQuotePreview quote={paymentQuote} isLoading={isLoadingQuote} error={quoteError} />
            </>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2, color: 'success.main', fontWeight: 600 }}>
                {t('payment_link_generated') || 'Enlace de donación generado y enviado por correo'}
              </DialogContentText>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('total_amount') || 'Monto total'}: <strong>${Number(generatedPayment.amount_total).toFixed(2)}</strong>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={generatedPayment.payment_url}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleCopyLink} size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('items') || 'Conceptos'}</Typography>
              <List dense>
                {generatedPayment.items.map((item, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText primary={item.description} secondary={`$${Number(item.amount).toFixed(2)}`} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>{t('close') || 'Cerrar'}</Button>
          {!generatedPayment && (
            <LoadingButton
              variant="contained"
              loading={isGeneratingPayment || isExpandingSiblings || isLoadingQuote}
              disabled={paymentRows.length === 0 || isExpandingSiblings || isLoadingQuote || !paymentQuote || Number(paymentQuote?.total) <= 0 || Boolean(quoteError)}
              onClick={handleGeneratePaymentLink}
              sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
            >
              {t('generate') || 'Generar'}
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>
    </Page>
  );
}
